import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import {
  fetchOptions,
  fetchPacks,
  requestPack,
} from "services/contract.services";
import { selectAuthToken, selectCurrentUser } from "store/auth/selectors";
import { useModal, useLanguage } from "context";

import { FormButton, FiltersDropdown } from "reusableComponents";
import { ComparePacksModal, ReusableConfirmationModal } from "modals";
import { iconKWhite, MemberPlusIcon } from "assets/icons";

import styles from "./styles.module.scss";
import OptionCard from "./OptionCard";
import PackCard from "./PackCard";

const MyContract = () => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { language } = useLanguage();
  const accessToken = useSelector(selectAuthToken);
  const user = useSelector(selectCurrentUser);

  const [packs, setPacks] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
      const [optionsData, packsData] = await Promise.all([
  fetchOptions(accessToken, language),
  fetchPacks(accessToken, language),
]);

        setOptions(optionsData);
        setPacks(initializePacks(packsData));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accessToken, language]);

  const initializePacks = (packsData) => {
    const defaultPack = {
      id: "member_plus",
      name: "Member Plus",
      image_url: MemberPlusIcon,
      description: t("memberplus.description"),
    };

    return [defaultPack, ...packsData].map((pack) => ({
      ...pack,
      active:
        pack.id === user?.user_pack?.base_pack_id ||
        (pack.name === "Member Plus" && user?.partner ? true : false),
    }));
  };

  const handleActivatePackConfirmation = (packId, status) => {
    if (status === true || status === "true") {
      openModal({
        content: (
          <ReusableConfirmationModal
            message={
              packId === "member_plus"
                ? t(
                    "dashboard.packsManagement.confirmationActivateTextMemberPlus"
                  )
                : t("dashboard.packsManagement.confirmationActivateText")
            }
            onConfirm={async () => activatePack(packId, status)}
          />
        ),
        title: t("dashboard.packsManagement.confirmationActivateTitle"),
      });
    } else {
      activatePack(packId, status);
    }
  };

  const activatePack = async (packId, status) => {
    setLoading(true);
    try {
      await requestPack(packId, accessToken, status);

      const updatedPacksData = await fetchPacks(accessToken);

      setPacks(initializePacks(updatedPacksData));

      if (status === true) {
        setTimeout(() => {
          openModal({
            content: <p>{t("myContract.informationModalText")}</p>,
            title: t("myContract.informationModalTitle"),
            type: "success",
            autoClose: true,
            closeDelay: 3000,
          });
        }, 500);
      }
    } catch (error) {
      console.error("Pack Request Failed:", error);
    } finally {
      setLoading(false);
    }
  };

  if (error) return <p>Error: {error}</p>;

  return (
    <div className={`${styles.myContract} ${loading ? styles.loading : ""}`}>
      <header className={styles.header}>
        <h2 className={styles.dashboardTitle}>
          {t("dashboard.provider.spaceManagement")}
        </h2>
        <div className={styles.comparePacksBtnHolder}>
          <FormButton
            label={t("dashboard.myContract.comparePacks")}
            onClick={() =>
              openModal({
                content: <ComparePacksModal />,
                title: t("dashboard.myContract.comparePacks"),
                type: "default",
                autoClose: false,
                closeDelay: 3000,
                size: "large", // Assuming 1000 corresponds to the desired modal size
              })
            }
            variant="gray"
          />
        </div>
      </header>

      <div className={styles.body}>
        <section className={styles.packs}>
          <div className={`${styles.itemsList} ${styles.packsList}`}>
            {packs.map((pack) => (
              <PackCard
                key={pack.id}
                pack={pack}
                onActivate={handleActivatePackConfirmation}
              />
            ))}
          </div>
        </section>

        <section className={styles.options}>
          <div className={`${styles.itemsList} ${styles.optionsList}`}>
            {options.map((option) => (
              <OptionCard key={option.id} option={option} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MyContract;
